/**
 * "Entrar con un código por mail" (session audit R4): the parts of the flow
 * that the server action and both login screens (classic and Encuentro) share.
 *
 * The code length is NOT fixed here. Supabase's `mailer_otp_length` is 8 in
 * production and 6 in staging today, and the length is a dashboard setting, so
 * the screens accept whatever the project sends: 6 to 10 digits. The server
 * action applies the same bounds.
 */
export const LOGIN_CODE_MIN_LENGTH = 6
export const LOGIN_CODE_MAX_LENGTH = 10

/** Seconds the screen waits before offering "Enviarme otro código". */
export const LOGIN_CODE_RESEND_SECONDS = 60

/** Digits only: a pasted "123 456" or "123-456" works. */
export function normalizeLoginCode(value: string): string {
  return value.replace(/\D/g, '').slice(0, LOGIN_CODE_MAX_LENGTH)
}

export function isPlausibleLoginCode(value: string): boolean {
  return (
    /^\d+$/.test(value) &&
    value.length >= LOGIN_CODE_MIN_LENGTH &&
    value.length <= LOGIN_CODE_MAX_LENGTH
  )
}

export type RequestLoginCodeResult =
  /** "If there is an account, a mail is on its way". The same for every address. */
  | { ok: true; cooldownSeconds: number }
  | { ok: false; reason: 'invalid_email' }
  | { ok: false; reason: 'rate_limited'; cooldownSeconds: number }
  /** Our side or Supabase's failed (5xx, network): says nothing about the address. */
  | { ok: false; reason: 'failed' }

/** Success is a redirect, so a returned result is always a failure. */
export type VerifyLoginCodeResult =
  | { ok: false; reason: 'invalid_code' }
  | { ok: false; reason: 'rate_limited'; cooldownSeconds: number }
  | { ok: false; reason: 'failed' }
  | { ok: false; reason: 'profile_error'; error: string }

/**
 * What the screen knows and the server cannot: whether the page named a school.
 * A flag only; the school itself never travels (lib/analytics/login-events.ts).
 */
export interface LoginCodeContext {
  hasInstitution?: boolean
}

export type RequestLoginCodeAction = (
  email: string,
  context?: LoginCodeContext
) => Promise<RequestLoginCodeResult>
export type VerifyLoginCodeAction = (
  input: {
    email: string
    code: string
    next?: string | null
  } & LoginCodeContext
) => Promise<VerifyLoginCodeResult | undefined | void>
